import ArrowDropDownIcon from '@mui/icons-material/ExpandMore';
import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import {
  GeoFenceDomainProps,
  GoogleMapProps,
  HSpacer,
  MarkerDomain,
  MarkerDomainProps,
  MarkerDomainTypes,
  RoutalLoader,
  RoutalPalette,
} from '@nx-smartmonkey/ui';
import hash from 'object-hash';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import SelectedStopsIcon from '../../../../assets/img/selected_stops_icon.svg';
import { StopsActionsMenu } from '../../../../components/menus/StopsActionsMenu';
import { getRouteColor } from '../../../../constants/Colors';
import DRIVER_STATUS from '../../../../constants/Drivers';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Driver } from '../../../../domain/driver/Driver';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { usePlanMapFilters } from '../../../../hooks/plan/usePlanMapFilters';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanGeoFences } from '../../../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanFocusedStop } from '../../../../hooks/plan/useSetActivePlanFocusedStop';
import { useSetActivePlanSelectedDrivers } from '../../../../hooks/plan/useSetActivePlanSelectedDrivers';
import { useSetActivePlanSelectedStops } from '../../../../hooks/plan/useSetActivePlanSelectedStops';
import { useSetPlanEditingGeoFence } from '../../../../hooks/plan/useSetPlanEditingGeoFence';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useSetActiveLanguage } from '../../../../hooks/translations/useSetActiveLanguage';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import { useRetrieveVehiclesAvatar } from '../../../../hooks/vehicle/useRetrieveVehiclesAvatar';
import { Map } from '../../../../react-components/Map';
import Text from '../../../../react-components/Text';
import { getLocationFromCoordinates } from '../../../../services/requests';
import { hasStopBrokenConstraints } from '../../shared/BrokenConstraintsTooltip';
import { hasAnyWarning } from '../../shared/OptimizationWarningsTooltip';
import { PlanGeoFencesActionsMenu } from '../PlanGeoFencesActionsMenu';
import { StopsFromScratchDialog } from '../dialogs/StopsFromScratchDialog';
import { MapContextMenu } from './MapContextMenu';
import { MapFilters } from './MapFilters';
import { MapGeoFences } from './MapGeoFences';
import { MapStopsSearcher } from './MapStopsSearcher';

type PlanMapGeoFenceContextAnchor = {
  screenPosition: { top: number; left: number };
  geoFence: GeoFence;
};

export type PlanDetailsMapContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
  coordinates: {
    lat: number;
    lng: number;
  };
};

interface PlanDetailsMapProps extends Omit<GoogleMapProps, `apiKey`> {
  contextualMenuOpen?: () => void;
}

export const PlanDetailsMap = ({ contextualMenuOpen, ...mapProps }: PlanDetailsMapProps) => {
  const intl = useIntl();
  const { detailType, setRightPanelInfo, cleanRightPanelInfo } = useRetrieveRightPanelInfo();

  const { data: activeLanguage } = useSetActiveLanguage();

  const { data: userCredentials } = useRetrieveUserCredentials();

  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: vehiclesAvatarMap } = useRetrieveVehiclesAvatar();

  const { data: stops, isLoading: isStopsLoading } = useRetrieveActivePlanStops();
  const { data: drivers, isLoading: isDriversLoading } = useRetrieveActivePlanDrivers();
  const { data: activePlanGeoFences } = useRetrieveActivePlanGeoFences();

  const { focusedStop, setFocusedStop } = useSetActivePlanFocusedStop();

  const { selectedStops, setSelectedStops, selectStopFromMap } = useSetActivePlanSelectedStops();
  const { selectedDrivers, selectDriverFromMap } = useSetActivePlanSelectedDrivers();

  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);
  const [mapContextAnchor, setMapContextAnchor] = useState<PlanDetailsMapContextAnchor | undefined>(undefined);
  const [geoFenceContextAnchor, setGeoFenceContextAnchor] = useState<PlanMapGeoFenceContextAnchor | undefined>(
    undefined
  );

  const [openCreateStopsDialog, setOpenCreateStopsDialog] = useState<boolean>(false);

  const { driversFilters, symbolsFilters } = usePlanMapFilters();

  const transportMode = useMemo(() => activePlan?.traffic_options?.mode, [activePlan?.traffic_options?.mode]);

  const planGeoFences = useMemo(() => (activePlanGeoFences ?? []).filter((geo) => geo.enabled), [activePlanGeoFences]);

  const geoFencesEnabled = useMemo(
    () => userCredentials?.subscription.test(`planner.geofences`) && activeProject?.geo_fences_enabled,
    [activeProject?.geo_fences_enabled, userCredentials?.subscription]
  );

  const { data: editingGeoFence, run: setEditingGeoFence } = useSetPlanEditingGeoFence();
  const [creatingGeoFence, setCreatingGeoFence] = useState<boolean>(false);

  const onContextualMenu = useCallback(
    async (event: any) => {
      try {
        const result = await getLocationFromCoordinates([{ lat: event.latLng.lat(), lng: event.latLng.lng() }]);
        setSelectedLocation(result[0]);
      } catch (error) {
        setSelectedLocation(undefined);
      }
      setMapContextAnchor({
        screenPosition: {
          top: event.domEvent.y,
          left: event.domEvent.x,
        },
        coordinates: {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        },
      });
      if (contextualMenuOpen) {
        contextualMenuOpen();
      }
    },
    [contextualMenuOpen]
  );

  const filterRoutes = useCallback(() => {
    return !symbolsFilters.lines
      ? []
      : (drivers ?? [])
          .filter((d) => driversFilters[d.id] === true)
          .map((driver: Driver, idx: number) => ({
            showArrows: symbolsFilters.arrows,
            path: driver.planned_track ?? [],
            color: getRouteColor(driver, drivers ?? []),
            selected: selectedDrivers.includes(driver.id),
            onClick: (e: any) =>
              selectDriverFromMap({ driverId: driver.id, withControlKey: e.domEvent.ctrlKey || e.domEvent.metaKey }),
            onDoubleClick: (e: any) => {
              setRightPanelInfo({
                detailType: `driver`,
                detailId: `${driver.id}`,
                detailTab: null,
              });
            },
          }));
  }, [
    drivers,
    driversFilters,
    selectDriverFromMap,
    selectedDrivers,
    setRightPanelInfo,
    symbolsFilters.arrows,
    symbolsFilters.lines,
  ]);

  const filterMarkers = useCallback(() => {
    return !symbolsFilters.markers
      ? []
      : [
          // DRIVERS MARKERS
          ...(drivers ?? [])
            .filter((d) => driversFilters[d.id] === true)
            .reduce((markersAmount: MarkerDomainProps[], driver: Driver, idx: number) => {
              const routeColor = getRouteColor(driver as Driver, drivers ?? []);

              const driverSpecificMarkers: Array<MarkerDomainProps> = [];

              // Driver last position marker
              if (driver.status === DRIVER_STATUS.inTransit && driver.last_position?.isValid()) {
                driverSpecificMarkers.push({
                  id: `${driver.id}-last-position`,
                  label: intl.formatMessage({ id: `planmap.driver.last_position` }),
                  color: routeColor,
                  isRouteSpecificMarker: true,
                  lat: driver.last_position!.lat!,
                  lng: driver.last_position!.lng!,
                  // orientation: driver.last_position?.orientation,
                  kind: vehiclesAvatarMap[driver.id] ? `avatar` : transportMode ?? `car`,
                  avatarUrl: vehiclesAvatarMap[driver.id],
                });
              }

              // Driver start location marker
              if (driver.start_location && driver.start_location.isValid()) {
                driverSpecificMarkers.push({
                  id: `${driver.id}-start-position`,
                  label: intl.formatMessage({ id: `planmap.driver.start_position` }),
                  color: routeColor,
                  isRouteSpecificMarker: true,
                  lat: driver.start_location!.lat!,
                  lng: driver.start_location!.lng!,
                  kind: `start`,
                });
              }

              // Driver end location marker
              if (driver.end_location && driver.end_location.isValid()) {
                driverSpecificMarkers.push({
                  id: `${driver.id}-end-position`,
                  label: intl.formatMessage({ id: `planmap.driver.end_position` }),
                  color: routeColor,
                  isRouteSpecificMarker: true,
                  lat: driver.end_location!.lat!,
                  lng: driver.end_location!.lng!,
                  kind: `end`,
                });
              }

              const driverStopsMarkers = driver.stops
                .filter((stop) => stop.location?.isValid())
                .map((stop: Stop) => ({
                  id: stop.id,
                  label: stop.label,
                  color: routeColor,
                  selected: selectedStops.includes(stop.id),
                  lat: stop.location!.lat!,
                  lng: stop.location!.lng!,
                  kind: stop.type === `delivery` ? `default` : (`pickup` as MarkerDomainTypes),
                  number: stop.order != null ? stop.order + 1 : undefined,
                  done: stop.status === STOP_STATUS.completed && !selectedStops.includes(stop.id),
                  incomplete: stop.status === STOP_STATUS.incomplete && !selectedStops.includes(stop.id),
                  error: stop.status === STOP_STATUS.canceled && !selectedStops.includes(stop.id),

                  ...(symbolsFilters.alerts
                    ? {
                        hasWarnings:
                          hasStopBrokenConstraints({
                            stop,
                            driver,
                            planGeoFences: activePlanGeoFences ?? [],
                            activeProject,
                            userCredentials,
                          }) ||
                          hasAnyWarning({
                            stop,
                            planDrivers: drivers ?? [],
                            planGeoFences: activePlanGeoFences ?? [],
                            activeProject,
                            userCredentials,
                          }),
                      }
                    : {}),
                }));

              return markersAmount.concat(driverStopsMarkers).concat(driverSpecificMarkers);
            }, []),

          // UNASSIGNED STOPS MARKERS
          ...(driversFilters[`unassigned`] === true
            ? (stops?.filter((stop: Stop) => !stop.driver_id && stop.location?.isValid()) ?? []).map((stop: Stop) => ({
                id: stop.id,
                label: stop.label,
                selected: selectedStops.includes(stop.id),
                lat: stop.location!.lat!,
                lng: stop.location!.lng!,
                kind: stop.type === `delivery` ? `default` : (`pickup` as MarkerDomainTypes),
                done: stop.status === STOP_STATUS.completed && selectedStops.includes(stop.id),
                incomplete: stop.status === STOP_STATUS.incomplete && selectedStops.includes(stop.id),
                error: stop.status === STOP_STATUS.canceled && selectedStops.includes(stop.id),

                ...(symbolsFilters.alerts
                  ? {
                      hasWarnings:
                        hasStopBrokenConstraints({
                          stop,
                          planGeoFences: activePlanGeoFences ?? [],
                          activeProject,
                          userCredentials,
                        }) ||
                        hasAnyWarning({
                          stop,
                          planDrivers: drivers ?? [],
                          planGeoFences: activePlanGeoFences ?? [],
                          activeProject,
                          userCredentials,
                        }),
                    }
                  : {}),
              }))
            : []),
        ];
  }, [
    activePlanGeoFences,
    activeProject,
    drivers,
    driversFilters,
    intl,
    selectedStops,
    stops,
    symbolsFilters.alerts,
    symbolsFilters.markers,
    transportMode,
    userCredentials,
    vehiclesAvatarMap,
  ]);

  const filterGeoFences = useCallback(() => {
    return !symbolsFilters.zones || !geoFencesEnabled
      ? []
      : planGeoFences.map(
          (geo) =>
            ({
              id: geo.id,
              paths: geo.coordinatesToObject(),
              color: geo.color.value,
              eventsUpdateHash: hash({
                id: geo.id,
                editingGeoFence: editingGeoFence?.id,
              }),
              onDoubleClick: (evt: any) => {
                if (!editingGeoFence) {
                  setRightPanelInfo({
                    detailType: `geoFence`,
                    detailId: geo.id,
                    detailTab: null,
                  });
                }
              },
              onContextualMenu: (ev: any, geoFenceId: string) => {
                if (!editingGeoFence) {
                  const geoFence = planGeoFences.find((geoFence) => geoFence.id === geoFenceId);
                  if (geoFence) {
                    ev.preventDefault();
                    setGeoFenceContextAnchor({
                      screenPosition: { top: ev.clientY, left: ev.clientX },
                      geoFence,
                    });
                  }
                }
              },
            } as GeoFenceDomainProps)
        );
  }, [editingGeoFence, geoFencesEnabled, planGeoFences, setRightPanelInfo, symbolsFilters.zones]);

  const onSelectionDrawn = (coordinates: { lat?: number; lng?: number }[]) => {
    if (!symbolsFilters.markers) {
      setSelectedStops([]);
      return;
    }

    const multipleSelection = stops!.filter((stop: Stop) => {
      // Not selecting the markers that has invalid coordinates
      if (!stop.location?.isValid()) {
        return false;
      }

      // Not selecting the markers that belongs to a hidden driver using the filters
      if (stop.driver_id && !driversFilters[stop.driver_id] === true) {
        return false;
      }

      // Not selecting the markers that are not assigned when unassigned stops are hidden using the filters
      if (!stop.driver_id && !driversFilters[`unassigned`] === true) {
        return false;
      }

      return MarkerDomain.isMarkerInsideCoordinates({
        markerCoords: { lat: stop.location!.lat!, lng: stop.location!.lng! },
        coordinates,
      });
    });
    setSelectedStops(multipleSelection.map((stop) => stop.id));
  };

  if (!activeProject || !activePlan || !organization) {
    return null;
  }

  if (isStopsLoading || isDriversLoading) {
    return (
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        style={{ height: `100%`, width: `calc(100% - 380px)` }}
      >
        <RoutalLoader width={60} />
      </Grid>
    );
  }

  if (editingGeoFence !== undefined || creatingGeoFence) {
    return (
      <MapGeoFences
        project={activeProject!}
        planGeoFences={planGeoFences}
        creatingGeoFence={creatingGeoFence}
        setCreatingGeoFence={setCreatingGeoFence}
      />
    );
  }

  return (
    <>
      <div
        style={{
          position: `relative`,
          width: `100%`,
          height: `100%`,
        }}
      >
        <MapStopsSearcher
          stops={stops}
          onSearchStopSelect={(stop) => {
            setFocusedStop(stop);
            selectStopFromMap({ stopId: stop.id });
          }}
        />

        <div
          style={{
            position: `absolute`,
            zIndex: 1,
            left: `4px`,
            bottom: `4px`,
            display: `flex`,
          }}
        >
          <MapFilters
            setCreatingGeoFence={(isCreating: boolean) => {
              if (isCreating && detailType === `geoFence`) {
                cleanRightPanelInfo();
              }
              setCreatingGeoFence(isCreating);
            }}
          />
        </div>

        <div style={{ width: `100%`, height: `100%` }}>
          <Map
            {...(focusedStop?.location?.isValid()
              ? {
                  center: focusedStop.location.toCoordinate().toObject(),
                  zoomLevel: 15,
                }
              : {})}
            height="100%"
            keyboard={false}
            reboundsControl
            zoomControl
            disableDoubleClickZoom
            language={activeLanguage?.locale ?? `en`}
            onDrawableSelection={onSelectionDrawn}
            onContextualMenu={onContextualMenu}
            routes={filterRoutes()}
            markers={filterMarkers()}
            geoFences={filterGeoFences()}
            markerOnClick={(e: any, id: string, isRouteSpecificMarker: boolean) => {
              if (!isRouteSpecificMarker) {
                selectStopFromMap({ stopId: id, withControlKey: e.domEvent.ctrlKey || e.domEvent.metaKey });
              } else {
                selectDriverFromMap({ driverId: id, withControlKey: e.domEvent.ctrlKey || e.domEvent.metaKey });
              }
            }}
            markerOnDoubleClick={(e: any, id: string, isRouteSpecificMarker: boolean) => {
              setRightPanelInfo({
                detailType: isRouteSpecificMarker ? `driver` : `stop`,
                detailId: `${id}`,
                detailTab: null,
              });
            }}
            {...mapProps}
          />
        </div>

        {mapContextAnchor ? (
          <MapContextMenu
            contextAnchor={mapContextAnchor}
            selectedLocation={selectedLocation}
            openCreateStopsDialog={setOpenCreateStopsDialog}
            closeContextMenu={() => {
              setMapContextAnchor(undefined);
            }}
          />
        ) : null}

        {geoFenceContextAnchor ? (
          <PlanGeoFencesActionsMenu
            type="contextual"
            contextAnchor={geoFenceContextAnchor}
            setContextAnchor={() => {
              setGeoFenceContextAnchor(undefined);
            }}
            showEdit={!editingGeoFence || (editingGeoFence as GeoFence)!.id !== geoFenceContextAnchor.geoFence.id}
            geoFences={[geoFenceContextAnchor.geoFence]}
            onEditGeoFence={(geoFence: GeoFence) => {
              if (detailType === `geoFence`) {
                cleanRightPanelInfo();
              }

              setEditingGeoFence(geoFence);
            }}
          />
        ) : null}

        {selectedStops && selectedStops.length > 1 && (
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-around"
            style={{
              position: `absolute`,
              top: `60px`,
              right: `30px`,
              maxWidth: `250px`,
              zIndex: `448`,
              fontSize: `14px`,
              borderRadius: `10px`,
              color: RoutalPalette.white,
              background: `${RoutalPalette.primary40} 0% 0% no-repeat padding-box`,
            }}
          >
            <StopsActionsMenu
              type="popover"
              stops={(stops ?? []).filter((s: Stop) => selectedStops.includes(s.id))}
              showOpenCustomers={userCredentials?.subscription.test(`customers`)}
              popoverProps={{
                hideArrow: true,
                style: {
                  height: `unset`,
                  width: `100%`,
                  margin: 0,
                  backgroundColor: RoutalPalette.primary40,
                },
                content: (
                  <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item container wrap="nowrap" justifyContent="center">
                      <img src={SelectedStopsIcon} width="18px" alt="" draggable="false" />
                      <HSpacer small />
                      <Text
                        variant="body"
                        style={{ color: `white`, cursor: `pointer` }}
                        intlId="planmap.selectedservices"
                        intlValues={{ number: selectedStops.length }}
                      />
                    </Grid>
                    <ArrowDropDownIcon style={{ color: `white` }} />
                  </Grid>
                ),
              }}
            />
          </Grid>
        )}
      </div>

      <StopsFromScratchDialog
        open={openCreateStopsDialog}
        initialLocation={selectedLocation}
        handleCloseDialog={() => setOpenCreateStopsDialog(false)}
      />
    </>
  );
};
